/**
 * GrowthWhatsAppMetrics — Growth Dashboard WhatsApp channel metrics
 * Controlled component: receives `days` prop from parent
 * Displays real metrics: conversations, orders, revenue, ticket avg, conversion rate
 * Includes period-over-period deltas
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  MessageSquare,
  ShoppingCart,
  TrendingUp,
  Euro,
  PercentIcon,
  RotateCw,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const VALID_PERIODS = new Set([1, 7, 30, 90]);

const SKELETON_CARD = (
  <div className="p-4 bg-gray-100 rounded animate-pulse">
    <div className="h-8 bg-gray-300 rounded w-2/3 mb-2"></div>
    <div className="h-4 bg-gray-300 rounded w-1/2"></div>
  </div>
);

export default function GrowthWhatsAppMetrics({ days = 30 }) {
  const { authAxios } = useAuth();
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Validate input days parameter
  const safeDays = VALID_PERIODS.has(days) ? days : 30;

  // Check if error is from cancellation (multiple axios versions)
  const isCancellationError = (err) => {
    return (
      err?.name === "AbortError" ||
      err?.name === "CanceledError" ||
      err?.code === "ERR_CANCELED" ||
      err?.message === "Cancelled" ||
      (typeof axios?.isCancel === "function" && axios.isCancel(err))
    );
  };

  const fetchMetrics = useCallback(async (fetchDays) => {
    // Increment request ID to track this specific request
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      setRefreshing(true);
      setError(null);
      const response = await authAxios.get(
        `/growth/whatsapp/metrics?days=${fetchDays}`,
        { signal: abortControllerRef.current.signal }
      );

      // Only update state if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setData(response.data);
      }
    } catch (err) {
      // Ignore cancellation errors - old request being aborted
      if (isCancellationError(err)) {
        return;
      }

      // Only update error state if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setError(
          err.response?.data?.detail ||
            "Erro ao carregar métricas WhatsApp"
        );
        setData(null);
      }
    } finally {
      // Only clear refreshing flag if this is still the latest request
      // This prevents old requests from clearing state while new ones are loading
      if (currentRequestId === requestIdRef.current) {
        setRefreshing(false);
      }
    }
  }, [authAxios]);

  useEffect(() => {
    setLoading(true);
    fetchMetrics(safeDays);

    // Cleanup: cancel pending request on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [authAxios, safeDays, fetchMetrics]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "€0.00";
    return `€${parseFloat(value).toFixed(2).replace(".", ",")}`;
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "0%";
    return `${parseFloat(value).toFixed(1).replace(".", ",")}%`;
  };

  const getDeltaColor = (delta) => {
    if (delta === null || delta === undefined || isNaN(delta))
      return "text-gray-600";
    return delta >= 0 ? "text-emerald-600" : "text-red-600";
  };

  const getDeltaIndicator = (delta) => {
    if (delta === null || delta === undefined || isNaN(delta))
      return "—";
    const sign = delta > 0 ? "+" : "";
    return `${sign}${delta}%`;
  };

  // Error state: distinguish from valid empty data
  if (error) {
    return (
      <div className="space-y-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => fetchMetrics(safeDays)}
              className="mt-3 px-3 py-1 text-sm font-medium bg-red-600 text-white rounded hover:bg-red-700"
            >
              Tentar novamente
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isReady = data && !refreshing;

  return (
    <div className="space-y-6">
      {/* Refreshing indicator */}
      {refreshing && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <RotateCw className="w-4 h-4 animate-spin" />
          Atualizando...
        </div>
      )}

      {/* Loading State */}
      {loading && !isReady && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>{SKELETON_CARD}</Card>
          ))}
        </div>
      )}

      {/* Data Display */}
      {isReady && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Conversas */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <MessageSquare className="w-4 h-4 text-blue-600" />
                  Conversas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {data?.whatsapp_conversations ?? 0}
                </div>
                <p className={`text-xs mt-1 ${getDeltaColor(data?.comparison?.conversations_delta_percent)}`}>
                  {getDeltaIndicator(data?.comparison?.conversations_delta_percent)}
                </p>
              </CardContent>
            </Card>

            {/* Pedidos */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <ShoppingCart className="w-4 h-4 text-purple-600" />
                  Pedidos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {data?.whatsapp_order_count ?? 0}
                </div>
                <p className={`text-xs mt-1 ${getDeltaColor(data?.comparison?.orders_delta_percent)}`}>
                  {getDeltaIndicator(data?.comparison?.orders_delta_percent)}
                </p>
              </CardContent>
            </Card>

            {/* Taxa de Conversão */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-orange-600" />
                  Taxa de Conversão
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatPercentage(data?.whatsapp_conversion_rate)}
                </div>
                <p className={`text-xs mt-1 ${getDeltaColor(data?.comparison?.conversion_delta_percent)}`}>
                  {getDeltaIndicator(data?.comparison?.conversion_delta_percent)}
                </p>
              </CardContent>
            </Card>

            {/* Receita */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Euro className="w-4 h-4 text-emerald-600" />
                  Receita
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(data?.whatsapp_revenue)}
                </div>
                <p className={`text-xs mt-1 ${getDeltaColor(data?.comparison?.revenue_delta_percent)}`}>
                  {getDeltaIndicator(data?.comparison?.revenue_delta_percent)}
                </p>
              </CardContent>
            </Card>

            {/* Ticket Médio */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <PercentIcon className="w-4 h-4 text-red-600" />
                  Ticket Médio
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(data?.whatsapp_avg_ticket)}
                </div>
                <p className="text-xs mt-1 text-gray-500">por pedido</p>
              </CardContent>
            </Card>
          </div>

          {/* Latency Info */}
          <div className="text-xs text-gray-500 text-right">
            {data?._latency_ms && `Latência: ${data._latency_ms}ms`}
          </div>
        </>
      )}
    </div>
  );
}
